import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIERS, daysLeft, formatRp } from "@/lib/tiers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Crown, Flame, CalendarClock, User as UserIcon, KeyRound, Receipt, Camera, Sparkles } from "lucide-react";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";
import Username from "@/components/Username";
import DiscordConnect from "@/components/DiscordConnect";
import BugReportButton from "@/components/BugReportButton";

export default function DashboardPage() {
  const { user, refresh } = useAuth();
  const [payments, setPayments] = useState([]);
  const [profile, setProfile] = useState({ name: user?.name || "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [quota, setQuota] = useState({ uploads_this_month: 0, uploads_remaining_this_month: 3 });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [totalContribution, setTotalContribution] = useState(0);
  const avatarInput = useRef(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/payments/mine"); setPayments(data.payments || []); }
      catch (e) { console.error("Failed to load payments:", e); }
      try { const { data } = await api.get("/uploads/avatar/quota"); setQuota(data); }
      catch (e) { /* ignore */ }
      try { const { data } = await api.get("/me/contributions/total"); setTotalContribution(data.total_contribution || 0); }
      catch (e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => { setProfile({ name: user?.name || "" }); }, [user]);

  const left = daysLeft(user?.membership_end);
  const tier = TIERS[user?.role] || TIERS.member;

  const saveProfile = async () => {
    try { await api.put("/auth/profile", profile); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQuota({
        uploads_this_month: data.uploads_this_month,
        uploads_remaining_this_month: data.uploads_remaining_this_month,
      });
      await refresh();
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploadingAvatar(false); }
  };

  const changePwd = async () => {
    try {
      await api.post("/auth/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Avatar user={user} size={80} />
              <button
                onClick={() => avatarInput.current?.click()}
                disabled={uploadingAvatar || (quota.uploads_remaining_this_month ?? 3) <= 0}
                className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 grid place-items-center text-xs text-white transition disabled:cursor-not-allowed"
                data-testid="avatar-change-btn"
                title={quota.uploads_remaining_this_month <= 0 ? "Monthly limit reached" : "Change profile picture"}
              >
                <Camera className="w-5 h-5" />
              </button>
              <input
                ref={avatarInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
                data-testid="avatar-file-input"
              />
            </div>
            <div>
              <div className="font-mystic text-rose-400 text-sm">Welcome back, cultist</div>
              <h1 className="font-display text-3xl font-black tracking-tight">
                <Username user={user} className={user?.role === "admin" ? "" : "text-white"} />
              </h1>
              <div className="text-zinc-400 text-sm mt-1">{user?.email}</div>
              <div className="mt-2"><RoleBadge role={user?.role} badges={user?.badges} size="xs" /></div>
            </div>
          </div>
          <Link to="/membership"><Button variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-950/30">Upgrade tier</Button></Link>
        </div>

        <div className="mt-3 text-xs text-zinc-500">
          Profile picture changes this month: <span className="text-zinc-300 font-semibold">{quota.uploads_this_month}</span> / 3
          {uploadingAvatar && <span className="ml-2 text-rose-300">Uploading…</span>}
        </div>

        {/* Status */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className={`p-6 rounded-xl border ${
            user?.role === "god_tier_supporter" ? "border-amber-400/50 shadow-[0_0_30px_rgba(251,191,36,0.25)]" :
            user?.role === "hardcore_cultist" ? "border-rose-500/40 glow-crimson" :
            user?.role === "elite_cultist" ? "border-violet-500/40 glow-violet" : "border-white/10"} bg-zinc-950`}>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Current Tier</div>
            <div className="mt-2 flex items-center gap-2">
              {user?.role === "god_tier_supporter" ? <Sparkles className="w-5 h-5 text-amber-300" /> :
               user?.role === "hardcore_cultist" ? <Flame className="w-5 h-5 text-rose-400" /> :
               user?.role === "elite_cultist" ? <Crown className="w-5 h-5 text-violet-400" /> :
               user?.role === "admin" ? <UserIcon className="w-5 h-5 text-amber-400" /> : <UserIcon className="w-5 h-5 text-zinc-400" />}
              <span className={`font-display text-2xl font-bold ${tier.color}`}>{tier.label}</span>
            </div>
          </div>
          <div className="p-6 rounded-xl border border-white/10 bg-zinc-950">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Days Remaining</div>
            <div className="mt-2 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-rose-400" />
              <span className="font-display text-2xl font-bold text-white">{left}</span>
              <span className="text-zinc-500 text-sm">days</span>
            </div>
            <div className="text-xs text-zinc-500 mt-2">
              {user?.membership_end ? `Expires ${new Date(user.membership_end).toLocaleDateString()}` : "No active subscription"}
            </div>
          </div>
          <div className="p-6 rounded-xl border border-white/10 bg-zinc-950">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Payment Submissions</div>
            <div className="mt-2 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-rose-400" />
              <span className="font-display text-2xl font-bold text-white">{payments.length}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-2">
              {payments.filter(p => p.status === "pending").length} pending
            </div>
          </div>
          <div className="p-6 rounded-xl border border-amber-400/30 bg-zinc-950 shadow-[0_0_24px_rgba(251,191,36,0.12)]" data-testid="dashboard-total-contribution">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Total Contributions</div>
            <div className="mt-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-300" />
              <span className="font-display text-2xl font-bold text-amber-200">{formatRp(totalContribution)}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-2">All-time across payments &amp; manual entries</div>
          </div>
        </div>

        <Tabs defaultValue="history" className="mt-10">
          <TabsList className="bg-zinc-950 border border-white/10">
            <TabsTrigger value="history" data-testid="tab-history">Payment History</TabsTrigger>
            <TabsTrigger value="discord" data-testid="tab-discord">Discord</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-5">
            <div className="glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-zinc-400 text-xs uppercase tracking-wider bg-black/40">
                    <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Tier</th><th className="text-left p-3">Method</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Admin Note</th></tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 && <tr><td colSpan={6} className="text-center p-8 text-zinc-500">No tributes yet.</td></tr>}
                    {payments.map((p) => (
                      <tr key={p.id} className="border-t border-white/5" data-testid={`payment-row-${p.id}`}>
                        <td className="p-3 text-zinc-300">{new Date(p.created_at).toLocaleString()}</td>
                        <td className="p-3 text-zinc-300">{TIERS[p.tier]?.label}</td>
                        <td className="p-3 text-zinc-300 uppercase">{p.method}</td>
                        <td className="p-3 text-white font-semibold">{formatRp(p.amount)}</td>
                        <td className="p-3">
                          <Badge className={
                            p.status === "approved" ? "bg-emerald-900/40 text-emerald-300 border border-emerald-500/30" :
                            p.status === "rejected" ? "bg-rose-900/40 text-rose-300 border border-rose-500/30" :
                            "bg-amber-900/40 text-amber-300 border border-amber-500/30"
                          }>{p.status}</Badge>
                        </td>
                        <td className="p-3 text-zinc-400">{p.admin_note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="discord" className="mt-5">
            <div className="space-y-5 max-w-3xl">
              <DiscordConnect />
              <div className="glass-strong rounded-xl p-6">
                <div className="font-display text-lg font-bold tracking-tight text-white mb-1">Something broken?</div>
                <p className="text-sm text-zinc-400 mb-4">
                  Spotted a bug or have feedback? Send a report straight to our Discord team.
                </p>
                <BugReportButton variant="outline" className="border-rose-500/40" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-5">
            <div className="glass-strong rounded-xl p-7 max-w-lg space-y-4">
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-wider">Name</Label>
                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1.5 bg-black/40 border-white/10" data-testid="profile-name-input" />
              </div>
              <Button onClick={saveProfile} className="bg-rose-600 hover:bg-rose-500" data-testid="profile-save-btn">Save</Button>
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-5">
            <div className="glass-strong rounded-xl p-7 max-w-lg space-y-4">
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-wider">Current Password</Label>
                <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="mt-1.5 bg-black/40 border-white/10" data-testid="security-current-pwd" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-wider">New Password</Label>
                <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="mt-1.5 bg-black/40 border-white/10" data-testid="security-new-pwd" />
              </div>
              <Button onClick={changePwd} className="bg-rose-600 hover:bg-rose-500" data-testid="security-change-pwd-btn"><KeyRound className="w-4 h-4 mr-2" />Change Password</Button>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </PageShell>
  );
}
